import {
  ArrowBack as BackIcon,
  AutoAwesome as AgentIcon,
  CheckCircle as CheckIcon,
  EmojiEvents as ContractIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  GroupWork as PartnerIcon,
  Gavel as RegulatoryIcon,
  Launch as LaunchIcon,
  LocationOn as LocationIcon,
  MergeType as MaIcon,
  People as HiringIcon,
  TrendingUp as InvestmentIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import type {
  AccountStreamMatch,
  DecisionEntry,
  FitRationaleSection,
  Signal,
  SignalType,
} from "../../types/accountIntelligence";
import { getMatchByAccountAndStream, getStreamById } from "./mockData";
import AccountStateChip from "./shared/AccountStateChip";
import OpportunityAngleCard from "./shared/OpportunityAngleCard";

// ─── Types ─────────────────────────────────────────────────────────────────────

type WorkspaceTab = "overview" | "profile" | "intelligence" | "history";

// ─── Signal helpers ────────────────────────────────────────────────────────────

const SIGNAL_ICONS: Record<SignalType, React.ReactNode> = {
  hiring: <HiringIcon sx={{ fontSize: 16 }} />,
  investment: <InvestmentIcon sx={{ fontSize: 16 }} />,
  partnership: <PartnerIcon sx={{ fontSize: 16 }} />,
  expansion: <LocationIcon sx={{ fontSize: 16 }} />,
  contract: <ContractIcon sx={{ fontSize: 16 }} />,
  event: <EventIcon sx={{ fontSize: 16 }} />,
  product_launch: <LaunchIcon sx={{ fontSize: 16 }} />,
  regulatory: <RegulatoryIcon sx={{ fontSize: 16 }} />,
  ma: <MaIcon sx={{ fontSize: 16 }} />,
};

const SIGNAL_TYPE_LABEL: Record<SignalType, string> = {
  hiring: "Hiring",
  investment: "Investment",
  partnership: "Partnership",
  expansion: "Expansion",
  contract: "Contract Award",
  event: "Event",
  product_launch: "Product Launch",
  regulatory: "Regulatory",
  ma: "M&A",
};

const FIT_COLOR: Record<string, string> = {
  high: "#2e7d32",
  medium: "#e65100",
  low: "#616161",
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Tab 1: Overview ──────────────────────────────────────────────────────────
// Angles are the headline. Recommended action follows. Latest signal anchors timing.

const OverviewTab: React.FC<{ match: AccountStreamMatch }> = ({ match }) => {
  const [dismissedAngles, setDismissedAngles] = useState<Set<string>>(new Set());
  const angles = match.angles.filter((a) => !dismissedAngles.has(a.id));

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Opportunity Angles — the core insight */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <AgentIcon sx={{ fontSize: 16, color: "primary.main" }} />
          <Typography variant="subtitle2" fontWeight={700} color="primary.main">
            OPPORTUNITY ANGLES
          </Typography>
          <Chip
            label={`${angles.length} active`}
            size="small"
            sx={{ fontSize: "0.65rem", height: 18, color: "text.secondary", ml: 0.5 }}
          />
        </Box>

        {angles.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2.5 }}
          >
            <Typography variant="body2" color="text.disabled">
              No opportunity angles identified yet. The agent is monitoring for relevant signals.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {angles.map((angle, i) => (
              <OpportunityAngleCard
                key={angle.id}
                angle={angle}
                defaultExpanded={i === 0}
                onDismiss={(id) =>
                  setDismissedAngles((prev) => new Set(Array.from(prev).concat(id)))
                }
                onPromote={() => undefined}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Latest signal */}
      {match.signals[0] && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
            LATEST SIGNAL
          </Typography>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                flexShrink: 0,
              }}
            >
              {SIGNAL_ICONS[match.signals[0].type]}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {match.signals[0].title}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.25}>
                {match.signals[0].description}
              </Typography>
              <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
                {formatDate(match.signals[0].date)} · {match.signals[0].source}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Account context — minimal, at the bottom */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          pt: 1,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {[
          ["Type", match.account.accountType],
          ["Industry", match.account.industry],
          ["Geography", match.account.geography],
          ["Size", match.account.size],
        ].map(([label, value]) => (
          <Box key={label}>
            <Typography variant="caption" color="text.disabled" fontWeight={700} display="block">
              {label.toUpperCase()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

// ─── Tab 2: Profile ───────────────────────────────────────────────────────────
// Generic org info as it would come from the knowledge graph.

const ProfileTab: React.FC<{ match: AccountStreamMatch }> = ({ match }) => {
  const { account } = match;
  const p = account.profile;

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Description */}
      {p?.description && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
            ABOUT
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
            {p.description}
          </Typography>
        </Box>
      )}

      {/* Key facts grid */}
      <Box>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
          KEY FACTS
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {[
            ["Account Type", account.accountType],
            ["Industry", account.industry],
            ["Headquarters", account.geography],
            ["Headcount", account.size],
            p?.founded ? ["Founded", p.founded] : null,
            p?.estimatedRevenue ? ["Revenue (est.)", p.estimatedRevenue] : null,
            account.website ? ["Website", account.website] : null,
            p?.hqAddress ? ["Address", p.hqAddress] : null,
          ]
            .filter((x): x is string[] => x !== null)
            .map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.disabled" fontWeight={700} display="block">
                  {label?.toUpperCase()}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
        </Box>
      </Box>

      {/* Technologies */}
      {p?.technologies && p.technologies.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
            TECHNOLOGIES & SYSTEMS
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {p.technologies.map((t) => (
              <Chip key={t} label={t} size="small" sx={{ fontSize: "0.75rem" }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Notable customers */}
      {p?.notableCustomers && p.notableCustomers.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
            NOTABLE CUSTOMERS
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {p.notableCustomers.map((c) => (
              <Chip
                key={c}
                label={c}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Certifications */}
      {p?.certifications && p.certifications.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>
            CERTIFICATIONS
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {p.certifications.map((c) => (
              <Chip
                key={c}
                label={c}
                size="small"
                sx={{ fontSize: "0.75rem", backgroundColor: "rgba(25,118,210,0.1)", color: "primary.main" }}
              />
            ))}
          </Box>
        </Box>
      )}

      {!p && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No profile data available. This will be populated from the knowledge graph.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

// ─── Tab 3: Intelligence ───────────────────────────────────────────────────────
// Signal timeline + fit rationale + ecosystem — all reference / trust-building content.

const SignalTimeline: React.FC<{ signals: Signal[] }> = ({ signals }) => {
  if (signals.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        No signals detected yet.
      </Typography>
    );
  }

  const sorted = [...signals].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <Stack spacing={0}>
      {sorted.map((signal, i) => {
        const relevanceColor = FIT_COLOR[signal.relevance];
        return (
          <Box key={signal.id} sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: `${relevanceColor}15`,
                  border: `1px solid ${relevanceColor}35`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: relevanceColor,
                }}
              >
                {SIGNAL_ICONS[signal.type]}
              </Box>
              {i < sorted.length - 1 && (
                <Box sx={{ width: 1, flex: 1, minHeight: 12, backgroundColor: "divider", my: 0.5 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, pb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
                <Typography variant="body2" fontWeight={700}>
                  {signal.title}
                </Typography>
                <Chip
                  label={SIGNAL_TYPE_LABEL[signal.type]}
                  size="small"
                  sx={{ fontSize: "0.65rem", height: 18 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {signal.description}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {formatDate(signal.date)} · {signal.source}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

const FitRationaleSection: React.FC<{ sections: FitRationaleSection[] }> = ({ sections }) => (
  <Stack spacing={1}>
    {sections.map((section) => {
      const color = FIT_COLOR[section.fitLevel];
      return (
        <Accordion
          key={section.label}
          elevation={0}
          disableGutters
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px !important",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", pr: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                {section.label}
              </Typography>
              <Chip
                size="small"
                label={section.fitLevel}
                sx={{
                  color,
                  backgroundColor: `${color}12`,
                  border: `1px solid ${color}35`,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  textTransform: "capitalize",
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {section.explanation}
            </Typography>
          </AccordionDetails>
        </Accordion>
      );
    })}
  </Stack>
);

const IntelligenceTab: React.FC<{ match: AccountStreamMatch }> = ({ match }) => (
  <Stack spacing={3} sx={{ p: 3 }}>
    {/* Signal timeline */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
        SIGNAL TIMELINE
      </Typography>
      <SignalTimeline signals={match.signals} />
    </Box>

    <Divider />

    {/* Why this account */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
        WHY THIS ACCOUNT
      </Typography>
      <FitRationaleSection sections={match.fitRationaleSections} />
    </Box>

    {/* Ecosystem */}
    {match.ecosystem.length > 0 && (
      <>
        <Divider />
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
            ECOSYSTEM CONTEXT
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                {["Relationship", "Entity", "Why it matters"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, fontSize: "0.72rem", color: "text.secondary" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {match.ecosystem.map((rel, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Chip label={rel.relationship} size="small" sx={{ fontSize: "0.68rem" }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {rel.entity}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {rel.whyItMatters}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </>
    )}
  </Stack>
);

// ─── Tab 3: History ───────────────────────────────────────────────────────────

const HistoryTab: React.FC<{ decisions: DecisionEntry[] }> = ({ decisions }) => {
  if (decisions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">No decisions recorded yet.</Typography>
      </Box>
    );
  }

  const sorted = [...decisions].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <Stack spacing={1.5} sx={{ p: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={0.5}>
        DECISION HISTORY
      </Typography>
      {sorted.map((entry, i) => (
        <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor:
                entry.actor === "user" ? "rgba(25,118,210,0.12)" : "rgba(46,125,50,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            {entry.actor === "user" ? (
              <CheckIcon sx={{ fontSize: 14, color: "primary.main" }} />
            ) : (
              <AgentIcon sx={{ fontSize: 14, color: "#2e7d32" }} />
            )}
          </Box>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 1.25,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" fontWeight={700}>
                  {entry.action}
                </Typography>
                <Chip
                  label={entry.actor === "user" ? "User" : "Agent"}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 16,
                    backgroundColor:
                      entry.actor === "user"
                        ? "rgba(25,118,210,0.1)"
                        : "rgba(46,125,50,0.1)",
                    color: entry.actor === "user" ? "primary.main" : "#2e7d32",
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.disabled">
                {formatDate(entry.date)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {entry.description}
            </Typography>
          </Paper>
        </Box>
      ))}
    </Stack>
  );
};

// ─── Main AccountWorkspace ─────────────────────────────────────────────────────

interface AccountWorkspaceProps {
  accountId: string;
  streamId: string;
  onBack: () => void;
}

const AccountWorkspace: React.FC<AccountWorkspaceProps> = ({ accountId, streamId, onBack }) => {
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("overview");

  const match = getMatchByAccountAndStream(accountId, streamId);
  const stream = getStreamById(streamId);

  if (!match || !stream) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Account or stream not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Tooltip title="Back to Portfolio">
          <IconButton size="small" onClick={onBack} sx={{ color: "text.secondary", mt: 0.25 }}>
            <BackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {match.account.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              {match.account.geography} · {match.account.industry} · {match.account.size}
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Chip
              label={stream.icp.name}
              size="small"
              sx={{ fontSize: "0.65rem", color: "text.secondary" }}
            />
          </Box>
        </Box>
        <AccountStateChip state={match.status} size="medium" />
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: WorkspaceTab) => setActiveTab(v)}
          sx={{ px: 2, minHeight: 40 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          {(
            [
              ["overview", "Overview"],
              ["profile", "Profile"],
              ["intelligence", "Intelligence"],
              ["history", "History"],
            ] as [WorkspaceTab, string][]
          ).map(([val, label]) => (
            <Tab
              key={val}
              value={val}
              label={label}
              sx={{ textTransform: "none", fontSize: "0.85rem", minHeight: 40, py: 0 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {activeTab === "overview" && <OverviewTab match={match} />}
        {activeTab === "profile" && <ProfileTab match={match} />}
        {activeTab === "intelligence" && <IntelligenceTab match={match} />}
        {activeTab === "history" && <HistoryTab decisions={match.decisions} />}
      </Box>
    </Box>
  );
};

export default AccountWorkspace;
